<template>
    <div class="wearables-dashboard">
        <!-- Connection Status Header -->
        <div class="dashboard-header">
            <h3>Wearables</h3>
            <button class="connect-btn" @click="showConnectModal = true" v-if="!hasConnectedDevice">
                <span class="btn-icon">+</span>
                Connect Device
            </button>
            <div v-else class="connected-badge">
                <span class="status-dot"></span>
                Connected
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Loading wearable data...</span>
        </div>

        <template v-else>
            <!-- No Device Connected -->
            <div v-if="!hasConnectedDevice" class="no-device">
                <div class="no-device-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.5">
                        <rect x="6" y="4" width="12" height="16" rx="2"></rect>
                        <line x1="10" y1="20" x2="14" y2="20"></line>
                    </svg>
                </div>
                <h4>No Wearable Connected</h4>
                <p>Connect your fitness tracker to sync activity, sleep, and health data automatically.</p>

                <div class="supported-devices">
                    <span class="device-badge" v-for="provider in topProviders" :key="provider.id">
                        {{ provider.name }}
                    </span>
                </div>

                <button class="btn-primary" @click="showConnectModal = true">
                    Connect Your Device
                </button>
            </div>

            <!-- Connected Device View -->
            <template v-else>
                <!-- Device Info -->
                <div class="device-info">
                    <div class="device-icon" :style="{ backgroundColor: connectedDevice.color }">
                        {{ connectedDevice.name.charAt(0) }}
                    </div>
                    <div class="device-details">
                        <span class="device-name">{{ connectedDevice.name }}</span>
                        <span class="last-sync">Last sync: {{ formatLastSync(connectedDevice.last_sync) }}</span>
                    </div>
                    <button class="sync-btn" @click="syncData" :disabled="syncing">
                        <span :class="{ spinning: syncing }">↻</span>
                    </button>
                </div>

                <!-- Today's Summary -->
                <div class="daily-summary">
                    <h4>Today's Summary</h4>

                    <div class="summary-grid">
                        <!-- Activity -->
                        <div class="summary-card activity">
                            <div class="card-header">
                                <span class="card-icon">👟</span>
                                <span class="card-title">Activity</span>
                            </div>
                            <div class="card-content">
                                <div class="main-metric">
                                    <span class="metric-value">{{ todaySummary.steps?.toLocaleString() || 0 }}</span>
                                    <span class="metric-label">steps</span>
                                </div>
                                <div class="secondary-metrics">
                                    <div class="metric">
                                        <span class="value">{{ todaySummary.distance_km?.toFixed(1) || 0 }}</span>
                                        <span class="label">km</span>
                                    </div>
                                    <div class="metric">
                                        <span class="value">{{ todaySummary.active_minutes || 0 }}</span>
                                        <span class="label">min</span>
                                    </div>
                                </div>
                            </div>
                            <div class="progress-bar">
                                <div class="progress" :style="{ width: stepsProgress + '%' }"></div>
                            </div>
                        </div>

                        <!-- Calories -->
                        <div class="summary-card calories">
                            <div class="card-header">
                                <span class="card-icon">🔥</span>
                                <span class="card-title">Calories</span>
                            </div>
                            <div class="card-content">
                                <div class="main-metric">
                                    <span class="metric-value">{{ todaySummary.calories_burned?.toLocaleString() || 0
                                    }}</span>
                                    <span class="metric-label">burned</span>
                                </div>
                                <div class="calorie-balance" v-if="todaySummary.calories_consumed">
                                    <span class="consumed">{{ todaySummary.calories_consumed }} in</span>
                                    <span class="balance" :class="calorieBalanceClass">
                                        {{ calorieBalance > 0 ? '+' : '' }}{{ calorieBalance }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Sleep -->
                        <div class="summary-card sleep">
                            <div class="card-header">
                                <span class="card-icon">😴</span>
                                <span class="card-title">Sleep</span>
                            </div>
                            <div class="card-content">
                                <div class="main-metric">
                                    <span class="metric-value">{{ formatSleepDuration(todaySummary.sleep_duration_mins)
                                    }}</span>
                                    <span class="metric-label">hrs</span>
                                </div>
                                <div class="sleep-quality" v-if="todaySummary.sleep_score">
                                    <div class="quality-badge" :class="sleepQualityClass">
                                        {{ sleepQualityLabel }}
                                    </div>
                                    <span class="score">{{ todaySummary.sleep_score }}%</span>
                                </div>
                            </div>
                        </div>

                        <!-- Heart Rate -->
                        <div class="summary-card heart">
                            <div class="card-header">
                                <span class="card-icon">❤️</span>
                                <span class="card-title">Heart Rate</span>
                            </div>
                            <div class="card-content">
                                <div class="main-metric">
                                    <span class="metric-value">{{ todaySummary.resting_hr || '--' }}</span>
                                    <span class="metric-label">bpm rest</span>
                                </div>
                                <div class="hr-range" v-if="todaySummary.min_hr && todaySummary.max_hr">
                                    <span>{{ todaySummary.min_hr }}</span>
                                    <span class="range-bar"></span>
                                    <span>{{ todaySummary.max_hr }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Training Load & Nutrition Adjustment -->
                <div class="training-nutrition-link" v-if="trainingLoad">
                    <div class="link-header">
                        <span class="link-icon">⚡</span>
                        <h4>Training Load Nutrition Adjustment</h4>
                    </div>

                    <div class="load-summary">
                        <div class="load-indicator" :class="trainingLoad.intensity.toLowerCase()">
                            <span class="load-value">{{ trainingLoad.loadScore }}</span>
                            <span class="load-label">Load Score</span>
                        </div>

                        <div class="load-details">
                            <div class="detail-row">
                                <span class="detail-label">Intensity</span>
                                <span class="detail-value" :style="{ color: trainingLoad.intensityColor }">
                                    {{ trainingLoad.intensity }}
                                </span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">Est. Calories Burned</span>
                                <span class="detail-value">{{ todaySummary.calories_burned || 0 }} kcal</span>
                            </div>
                        </div>
                    </div>

                    <div class="nutrition-adjustment" v-if="caloricAdjustment">
                        <div class="adjustment-card">
                            <span class="adjustment-icon">🍽️</span>
                            <div class="adjustment-info">
                                <span class="adjustment-label">Recommended Intake Today</span>
                                <span class="adjustment-value">
                                    {{ caloricAdjustment.recommended_calories }} kcal
                                </span>
                            </div>
                            <span class="adjustment-change" :class="{ positive: caloricAdjustment.change > 0 }">
                                {{ caloricAdjustment.change > 0 ? '+' : '' }}{{ caloricAdjustment.change }} kcal
                            </span>
                        </div>

                        <div class="macro-adjustments">
                            <div class="macro-item carbs">
                                <span class="macro-label">Carbs</span>
                                <span class="macro-target">{{ caloricAdjustment.carbs_g }}g</span>
                            </div>
                            <div class="macro-item protein">
                                <span class="macro-label">Protein</span>
                                <span class="macro-target">{{ caloricAdjustment.protein_g }}g</span>
                            </div>
                            <div class="macro-item fat">
                                <span class="macro-label">Fat</span>
                                <span class="macro-target">{{ caloricAdjustment.fat_g }}g</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Recent Workouts -->
                <div class="recent-workouts" v-if="recentWorkouts.length > 0">
                    <h4>Recent Workouts</h4>
                    <div class="workout-list">
                        <div v-for="workout in recentWorkouts" :key="workout.id" class="workout-item">
                            <div class="workout-icon"
                                :style="{ backgroundColor: getActivityColor(workout.activity_type) }">
                                {{ getActivityIcon(workout.activity_type) }}
                            </div>
                            <div class="workout-info">
                                <span class="workout-type">{{ workout.activity_type }}</span>
                                <span class="workout-date">{{ formatWorkoutDate(workout.start_time) }}</span>
                            </div>
                            <div class="workout-stats">
                                <span class="stat">{{ formatDuration(workout.duration_seconds) }}</span>
                                <span class="stat">{{ workout.calories }} kcal</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </template>

        <!-- Connect Device Modal -->
        <div v-if="showConnectModal" class="modal-overlay" @click.self="showConnectModal = false">
            <div class="connect-modal">
                <div class="modal-header">
                    <h3>Connect Your Device</h3>
                    <button class="close-btn" @click="showConnectModal = false">×</button>
                </div>

                <p class="modal-description">
                    Select your wearable device to sync your activity, sleep, and health data automatically.
                </p>

                <!-- Error Message -->
                <div v-if="error" class="modal-error">
                    <span class="error-icon">⚠️</span>
                    <span>{{ error }}</span>
                    <button class="dismiss-btn" @click="error = null">×</button>
                </div>

                <!-- Connecting State -->
                <div v-if="connecting" class="connecting-state">
                    <div class="spinner"></div>
                    <span>Connecting to {{ connectingProvider?.name || 'device' }}...</span>
                </div>

                <div class="provider-list" v-show="!connecting">
                    <button v-for="provider in providers" :key="provider.id" class="provider-btn"
                        @click="connectProvider(provider)" :disabled="connecting">
                        <div class="provider-icon" :style="{ backgroundColor: provider.color }">
                            {{ getProviderAbbreviation(provider.name) }}
                        </div>
                        <div class="provider-info">
                            <span class="provider-name">{{ provider.name }}</span>
                            <span class="provider-type">Fitness Tracker</span>
                        </div>
                        <span class="arrow">→</span>
                    </button>
                </div>

                <p class="privacy-note">
                    <span class="lock-icon">🔒</span>
                    Your data is securely synced via Terra. We only access fitness and health metrics.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { TerraWearables } from "@/services/terra-wearables-service";

export default {
    name: "WearablesDashboard",

    props: {
        userId: {
            type: [Number, String],
            required: true,
        },
        playerWeight: {
            type: Number,
            default: 70,
        },
        activityContext: {
            type: String,
            default: "training_day",
        },
    },

    data() {
        return {
            loading: true,
            syncing: false,
            connecting: false,
            connectingProvider: null,
            showConnectModal: false,
            connectedDevice: null,
            todaySummary: {},
            trainingLoad: null,
            caloricAdjustment: null,
            recentWorkouts: [],
            providers: [],
            error: null,
        };
    },

    computed: {
        hasConnectedDevice() {
            return !!this.connectedDevice;
        },

        topProviders() {
            return this.providers.slice(0, 4);
        },

        stepsProgress() {
            const goal = 10000;
            const steps = this.todaySummary.steps || 0;
            return Math.min(100, (steps / goal) * 100);
        },

        calorieBalance() {
            const burned = this.todaySummary.calories_burned || 0;
            const consumed = this.todaySummary.calories_consumed || 0;
            return consumed - burned;
        },

        calorieBalanceClass() {
            if (this.calorieBalance > 500) return "surplus";
            if (this.calorieBalance < -500) return "deficit";
            return "balanced";
        },

        sleepQualityLabel() {
            const score = this.todaySummary.sleep_score || 0;
            const quality = TerraWearables.getSleepQualityLabel(score);
            return quality.label;
        },

        sleepQualityClass() {
            const score = this.todaySummary.sleep_score || 0;
            if (score >= 85) return "excellent";
            if (score >= 70) return "good";
            if (score >= 50) return "fair";
            return "Unsatisfactory";
        },
    },

    mounted() {
        this.providers = TerraWearables.getSupportedProviders();
        this.checkPendingConnection();
        this.loadData();
    },

    methods: {
        checkPendingConnection() {
            // Check if user was redirected back from Terra widget
            const pending = localStorage.getItem('wearable_connect_pending');
            if (pending) {
                try {
                    const data = JSON.parse(pending);
                    // Only process if pending connection is less than 10 minutes old
                    if (Date.now() - data.timestamp < 600000) {
                        // Start polling for connection
                        this.listenForConnection();
                    }
                } catch (e) {
                    console.error('Error parsing pending connection:', e);
                }
                // Clear the pending state
                localStorage.removeItem('wearable_connect_pending');
            }
        },

        async loadData() {
            this.loading = true;
            try {
                // Check connection status
                const connectionResponse = await TerraWearables.checkConnectionStatus(this.userId);
                if (connectionResponse.data?.success && connectionResponse.data?.connected) {
                    this.connectedDevice = connectionResponse.data.device;
                    await this.loadWearableData();
                }
            } catch (error) {
                console.error("Error loading wearable data:", error);
            } finally {
                this.loading = false;
            }
        },

        async loadWearableData() {
            try {
                // Get daily summary
                const summaryResponse = await TerraWearables.getDailySummary(this.userId);
                if (summaryResponse.data?.success) {
                    this.todaySummary = summaryResponse.data.summary || {};

                    // Calculate training load if activity data available
                    if (this.todaySummary.activity_data) {
                        this.trainingLoad = TerraWearables.calculateTrainingLoad(this.todaySummary.activity_data);
                    }
                }

                // Get caloric needs based on activity
                const caloricResponse = await TerraWearables.calculateCaloricNeeds(
                    this.userId,
                    new Date().toISOString().split("T")[0],
                    {
                        weight: this.playerWeight,
                        activityContext: this.activityContext,
                        trainingLoad: this.trainingLoad?.loadScore || null,
                    }
                );
                if (caloricResponse.data?.success) {
                    this.caloricAdjustment = caloricResponse.data.recommendation;
                }

                // Get recent workouts
                const today = new Date();
                const weekAgo = new Date(today);
                weekAgo.setDate(weekAgo.getDate() - 7);

                const workoutsResponse = await TerraWearables.getWorkoutData(
                    this.userId,
                    weekAgo.toISOString().split("T")[0],
                    today.toISOString().split("T")[0]
                );
                if (workoutsResponse.data?.success) {
                    this.recentWorkouts = (workoutsResponse.data.workouts || []).slice(0, 5);
                }
            } catch (error) {
                console.error("Error loading wearable data:", error);
            }
        },

        async syncData() {
            this.syncing = true;
            try {
                await this.loadWearableData();
            } finally {
                this.syncing = false;
            }
        },

        async connectProvider(provider) {
            this.connecting = true;
            this.connectingProvider = provider;
            this.error = null;

            try {
                const response = await TerraWearables.generateWidgetSession(
                    this.userId,
                    window.location.href
                );

                console.log('Widget session response:', response);
                console.log('Response data:', response?.data);

                // Handle different response structures (axios wraps in data, direct response may not)
                const responseData = response?.data?.data || response?.data || response;
                const widgetUrl = responseData?.url || responseData?.session_url || responseData?.widget_url;
                const isSuccess = responseData?.success !== false; // Treat undefined as success if URL exists

                console.log('Parsed response - URL:', widgetUrl, 'Success:', isSuccess);

                if (widgetUrl && isSuccess) {
                    // Check if on mobile - use redirect instead of popup
                    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

                    if (isMobile) {
                        // Store state to localStorage so we can resume after redirect
                        localStorage.setItem('wearable_connect_pending', JSON.stringify({
                            userId: this.userId,
                            provider: provider.id,
                            timestamp: Date.now()
                        }));
                        // Redirect to Terra widget
                        window.location.href = widgetUrl;
                    } else {
                        // Desktop: try popup first, fallback to redirect
                        const popup = window.open(widgetUrl, "_blank", "width=500,height=700");

                        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                            // Popup was blocked - use redirect instead
                            localStorage.setItem('wearable_connect_pending', JSON.stringify({
                                userId: this.userId,
                                provider: provider.id,
                                timestamp: Date.now()
                            }));
                            window.location.href = widgetUrl;
                        } else {
                            // Popup opened successfully - listen for connection
                            this.listenForConnection();
                            this.connecting = false;
                            this.connectingProvider = null;
                        }
                    }
                } else {
                    // API returned but no URL - show detailed error
                    const errorMsg = responseData?.message || responseData?.error || 'No connection URL received from server.';
                    console.error('Widget session failed:', responseData);
                    this.error = errorMsg + ' Please try again or contact support.';
                    this.connecting = false;
                    this.connectingProvider = null;
                }
            } catch (error) {
                console.error("Error connecting device:", error);
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                this.error = `Failed to connect device: ${errorMessage}. Please check your connection and try again.`;
                this.connecting = false;
                this.connectingProvider = null;
            }
        },

        listenForConnection() {
            // Poll for connection status
            const checkInterval = setInterval(async () => {
                try {
                    const response = await TerraWearables.checkConnectionStatus(this.userId);
                    if (response.data?.success && response.data?.connected) {
                        clearInterval(checkInterval);
                        this.connectedDevice = response.data.device;
                        this.showConnectModal = false;
                        await this.loadWearableData();
                    }
                } catch (error) {
                    // Continue polling
                }
            }, 3000);

            // Stop polling after 5 minutes
            setTimeout(() => {
                clearInterval(checkInterval);
            }, 300000);
        },

        formatLastSync(timestamp) {
            if (!timestamp) return "Never";
            const date = new Date(timestamp);
            const now = new Date();
            const diff = now - date;
            const minutes = Math.floor(diff / 60000);

            if (minutes < 1) return "Just now";
            if (minutes < 60) return `${minutes}m ago`;
            if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
            return date.toLocaleDateString("en-GB");
        },

        formatSleepDuration(minutes) {
            if (!minutes) return "0";
            const hours = minutes / 60;
            return hours.toFixed(1);
        },

        formatDuration(seconds) {
            return TerraWearables.formatDuration(seconds);
        },

        formatWorkoutDate(timestamp) {
            if (!timestamp) return "";
            const date = new Date(timestamp);
            return date.toLocaleDateString("en-GB", {
                weekday: "short",
                day: "numeric",
                month: "short",
            });
        },

        getActivityIcon(activityType) {
            const info = TerraWearables.getActivityTypeInfo(activityType);
            return info.icon;
        },

        getActivityColor(activityType) {
            const info = TerraWearables.getActivityTypeInfo(activityType);
            return info.color;
        },

        getProviderAbbreviation(name) {
            const abbreviations = {
                'Fitbit': 'FIT',
                'Garmin': 'GAR',
                'Apple Health': 'APL',
                'Google Fit': 'GFT',
                'Samsung Health': 'SAM',
                'Whoop': 'WHP',
                'Oura': 'ORA',
                'Polar': 'POL',
                'Suunto': 'STO',
                'Withings': 'WTH',
                'COROS': 'COR',
                'Huawei': 'HUA',
                'Xiaomi': 'XMI',
                'Amazfit': 'AMZ',
            };
            return abbreviations[name] || name.substring(0, 3).toUpperCase();
        },
    },
};
</script>

<style lang="scss" scoped>
.wearables-dashboard {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
}

.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .connect-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        border: 2px dashed #28a745;
        border-radius: 8px;
        background: transparent;
        color: #28a745;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background: rgba(40, 167, 69, 0.1);
        }

        .btn-icon {
            font-size: 1.1rem;
        }
    }

    .connected-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background: #d4edda;
        border-radius: 16px;
        color: #28a745;
        font-size: 0.85rem;
        font-weight: 500;

        .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #28a745;
        }
    }
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    color: #666;

    .spinner {
        width: 32px;
        height: 32px;
        border: 3px solid #eee;
        border-top-color: #28a745;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 12px;
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.no-device {
    text-align: center;
    padding: 30px 20px;

    .no-device-icon {
        color: #ccc;
        margin-bottom: 16px;
    }

    h4 {
        margin: 0 0 8px 0;
        font-size: 1.1rem;
    }

    p {
        color: #666;
        font-size: 0.9rem;
        margin: 0 0 20px 0;
    }

    .supported-devices {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
        margin-bottom: 20px;

        .device-badge {
            padding: 4px 10px;
            background: #f0f0f0;
            border-radius: 12px;
            font-size: 0.75rem;
            color: #666;
        }
    }

    .btn-primary {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        background: linear-gradient(135deg, #28a745, #20c997);
        color: white;
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.2s;

        &:hover {
            transform: translateY(-2px);
        }
    }
}

.device-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 10px;
    margin-bottom: 16px;

    .device-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 1.1rem;
    }

    .device-details {
        flex: 1;

        .device-name {
            display: block;
            font-weight: 600;
        }

        .last-sync {
            font-size: 0.8rem;
            color: #666;
        }
    }

    .sync-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: white;
        cursor: pointer;
        font-size: 1.2rem;
        transition: all 0.2s;

        &:hover:not(:disabled) {
            background: #e9ecef;
        }

        &:disabled {
            opacity: 0.6;
        }

        .spinning {
            display: inline-block;
            animation: spin 1s linear infinite;
        }
    }
}

.daily-summary {
    margin-bottom: 20px;

    h4 {
        margin: 0 0 12px 0;
        font-size: 0.95rem;
        color: #666;
    }
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.summary-card {
    padding: 14px;
    border-radius: 12px;
    background: #f8f9fa;

    .card-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;

        .card-icon {
            font-size: 1.1rem;
        }

        .card-title {
            font-size: 0.8rem;
            font-weight: 600;
            color: #666;
        }
    }

    .card-content {
        .main-metric {
            margin-bottom: 6px;

            .metric-value {
                font-size: 1.4rem;
                font-weight: 700;
            }

            .metric-label {
                font-size: 0.75rem;
                color: #999;
                margin-left: 4px;
            }
        }
    }

    &.activity {
        .main-metric .metric-value {
            color: #28a745;
        }

        .secondary-metrics {
            display: flex;
            gap: 12px;

            .metric {
                .value {
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .label {
                    font-size: 0.7rem;
                    color: #999;
                    margin-left: 2px;
                }
            }
        }

        .progress-bar {
            height: 4px;
            background: #e0e0e0;
            border-radius: 2px;
            margin-top: 10px;
            overflow: hidden;

            .progress {
                height: 100%;
                background: linear-gradient(90deg, #28a745, #20c997);
                border-radius: 2px;
                transition: width 0.3s;
            }
        }
    }

    &.calories {
        .main-metric .metric-value {
            color: #fd7e14;
        }

        .calorie-balance {
            font-size: 0.8rem;

            .consumed {
                color: #666;
            }

            .balance {
                margin-left: 8px;
                font-weight: 600;

                &.surplus {
                    color: #dc3545;
                }

                &.deficit {
                    color: #28a745;
                }

                &.balanced {
                    color: #666;
                }
            }
        }
    }

    &.sleep {
        .main-metric .metric-value {
            color: #6f42c1;
        }

        .sleep-quality {
            display: flex;
            align-items: center;
            gap: 8px;

            .quality-badge {
                padding: 2px 8px;
                border-radius: 10px;
                font-size: 0.7rem;
                font-weight: 600;

                &.excellent {
                    background: #d4edda;
                    color: #28a745;
                }

                &.good {
                    background: #d1ecf1;
                    color: #17a2b8;
                }

                &.fair {
                    background: #fff3cd;
                    color: #856404;
                }

                &.poor {
                    background: #f8d7da;
                    color: #dc3545;
                }
            }

            .score {
                font-size: 0.8rem;
                color: #666;
            }
        }
    }

    &.heart {
        .main-metric .metric-value {
            color: #dc3545;
        }

        .hr-range {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.8rem;
            color: #666;

            .range-bar {
                flex: 1;
                height: 2px;
                background: linear-gradient(90deg, #28a745, #ffc107, #dc3545);
                border-radius: 1px;
            }
        }
    }
}

.training-nutrition-link {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;

    .link-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;

        .link-icon {
            font-size: 1.2rem;
        }

        h4 {
            margin: 0;
            font-size: 0.95rem;
        }
    }

    .load-summary {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        .load-indicator {
            width: 70px;
            height: 70px;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            &.low {
                background: rgba(40, 167, 69, 0.2);
            }

            &.medium {
                background: rgba(255, 193, 7, 0.2);
            }

            &.high {
                background: rgba(220, 53, 69, 0.2);
            }

            .load-value {
                font-size: 1.4rem;
                font-weight: 700;
            }

            .load-label {
                font-size: 0.65rem;
                color: #666;
            }
        }

        .load-details {
            flex: 1;

            .detail-row {
                display: flex;
                justify-content: space-between;
                padding: 6px 0;
                border-bottom: 1px solid rgba(0, 0, 0, 0.05);

                &:last-child {
                    border-bottom: none;
                }

                .detail-label {
                    font-size: 0.85rem;
                    color: #666;
                }

                .detail-value {
                    font-weight: 600;
                    font-size: 0.85rem;
                }
            }
        }
    }

    .nutrition-adjustment {
        .adjustment-card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            background: white;
            border-radius: 10px;
            margin-bottom: 12px;

            .adjustment-icon {
                font-size: 1.3rem;
            }

            .adjustment-info {
                flex: 1;

                .adjustment-label {
                    display: block;
                    font-size: 0.75rem;
                    color: #666;
                }

                .adjustment-value {
                    font-size: 1.2rem;
                    font-weight: 700;
                }
            }

            .adjustment-change {
                padding: 4px 10px;
                border-radius: 12px;
                font-size: 0.8rem;
                font-weight: 600;
                background: #f8d7da;
                color: #dc3545;

                &.positive {
                    background: #d4edda;
                    color: #28a745;
                }
            }
        }

        .macro-adjustments {
            display: flex;
            gap: 10px;

            .macro-item {
                flex: 1;
                text-align: center;
                padding: 10px;
                border-radius: 8px;

                .macro-label {
                    display: block;
                    font-size: 0.7rem;
                    color: #666;
                    margin-bottom: 4px;
                }

                .macro-target {
                    font-weight: 700;
                    font-size: 1rem;
                }

                &.carbs {
                    background: rgba(255, 193, 7, 0.15);

                    .macro-target {
                        color: #cc9a06;
                    }
                }

                &.protein {
                    background: rgba(220, 53, 69, 0.15);

                    .macro-target {
                        color: #dc3545;
                    }
                }

                &.fat {
                    background: rgba(40, 167, 69, 0.15);

                    .macro-target {
                        color: #28a745;
                    }
                }
            }
        }
    }
}

.recent-workouts {
    h4 {
        margin: 0 0 12px 0;
        font-size: 0.95rem;
        color: #666;
    }
}

.workout-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.workout-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 8px;

    .workout-icon {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 0.8rem;
    }

    .workout-info {
        flex: 1;

        .workout-type {
            display: block;
            font-weight: 600;
            font-size: 0.9rem;
            text-transform: capitalize;
        }

        .workout-date {
            font-size: 0.75rem;
            color: #666;
        }
    }

    .workout-stats {
        text-align: right;

        .stat {
            display: block;
            font-size: 0.8rem;
            color: #666;

            &:first-child {
                font-weight: 600;
                color: #333;
            }
        }
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.connect-modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 400px;
    max-height: 90vh;
    overflow-y: auto;

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-bottom: 1px solid #eee;

        h3 {
            margin: 0;
            font-size: 1.1rem;
        }

        .close-btn {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: none;
            background: #f0f0f0;
            font-size: 1.2rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .modal-description {
        padding: 16px;
        margin: 0;
        color: #666;
        font-size: 0.9rem;
    }

    .modal-error {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        margin: 0 16px 12px;
        background: #f8d7da;
        border-radius: 8px;
        color: #721c24;
        font-size: 0.9rem;

        .error-icon {
            flex-shrink: 0;
        }

        .dismiss-btn {
            margin-left: auto;
            padding: 4px 8px;
            border: none;
            background: transparent;
            cursor: pointer;
            font-size: 1.1rem;
            color: #721c24;
        }
    }

    .connecting-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        text-align: center;
        gap: 16px;

        .spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #28a745;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        span {
            color: #666;
            font-size: 0.95rem;
        }
    }

    .provider-list {
        padding: 0 16px 16px;
    }

    .provider-btn {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border: 1px solid #eee;
        border-radius: 10px;
        background: white;
        cursor: pointer;
        margin-bottom: 8px;
        transition: all 0.2s;

        &:hover:not(:disabled) {
            border-color: #28a745;
            background: rgba(40, 167, 69, 0.05);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .provider-icon {
            width: 44px;
            height: 44px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            font-size: 0.75rem;
            letter-spacing: -0.5px;
            flex-shrink: 0;
        }

        .provider-name {
            flex: 1;
            text-align: left;
            font-weight: 600;
            font-size: 1rem;
        }

        .provider-info {
            flex: 1;
            text-align: left;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .provider-name {
                font-weight: 600;
                font-size: 1rem;
                color: #333;
            }

            .provider-type {
                font-size: 0.75rem;
                color: #888;
            }
        }

        .arrow {
            color: #999;
        }
    }

    .privacy-note {
        padding: 12px 16px 16px;
        margin: 0;
        font-size: 0.8rem;
        color: #666;
        display: flex;
        align-items: center;
        gap: 8px;
        background: #f8f9fa;
        border-radius: 0 0 16px 16px;
    }
}
</style>
